/**
 * Helpers for building multipart FormData from form values that may include
 * image data URLs (from ImageUploadField) or nullable optional fields.
 */

const DATA_URL_RE = /^data:([^;]+);base64,(.+)$/;

export function dataUrlToFile(
  dataUrl: string,
  filename = 'upload',
): File | null {
  const match = DATA_URL_RE.exec(dataUrl);
  if (!match) {
    return null;
  }
  const mime = match[1];
  const binary = atob(match[2]);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const ext = mime.split('/')[1]?.split('+')[0] || 'bin';
  const name = filename.includes('.') ? filename : `${filename}.${ext}`;
  return new File([bytes], name, { type: mime });
}

/** True when a form value should not be sent (null/undefined/blank/"null"). */
export function isOmitFormDataValue(value: unknown): boolean {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' || trimmed === 'null' || trimmed === 'undefined';
  }
  return false;
}

/**
 * Append a value to FormData. Data-URL images become File blobs.
 * Existing http(s) URLs are skipped (server already has the file).
 * Null/blank/"null" strings are omitted.
 */
export function appendFormDataValue(
  formData: FormData,
  key: string,
  value: unknown,
): void {
  if (isOmitFormDataValue(value)) {
    return;
  }

  if (typeof value === 'string') {
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return;
    }
    if (value.startsWith('data:')) {
      const file = dataUrlToFile(value, key);
      if (file) {
        formData.append(key, file);
      }
      return;
    }
    formData.append(key, value);
    return;
  }

  if (value instanceof Blob) {
    formData.append(key, value);
    return;
  }

  if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
    formData.append(key, JSON.stringify(value));
    return;
  }

  formData.append(key, String(value));
}
